interface Storage<T> {
  get(): T | null
  set(value: T): void
  clear(): void
}

export class LocalStorageService<T> implements Storage<T> {
  constructor(private readonly key: string) {}

  get(): T | null {
    try {
      const item = localStorage.getItem(this.key)
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.error(`Failed to read from localStorage: ${this.key}`, error)
      return null
    }
  }

  set(value: T): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(value))
    } catch (error) {
      console.error(`Failed to write to localStorage: ${this.key}`, error)
      // Handle QuotaExceededError
      if (error instanceof DOMException && error.code === 22) {
        this.handleQuotaExceeded()
      }
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.key)
    } catch (error) {
      console.error(`Failed to clear localStorage: ${this.key}`, error)
    }
  }

  private handleQuotaExceeded(): void {
    // For AI history, we can remove old items when quota is exceeded
    console.warn('LocalStorage quota exceeded, attempting to clear old data')
    try {
      const stored = localStorage.getItem(this.key)
      if (stored) {
        const data = JSON.parse(stored)
        if (Array.isArray(data) && data.length > 0) {
          // Keep only the most recent half of items
          const halfLength = Math.floor(data.length / 2)
          const recentData = data.slice(0, halfLength)
          localStorage.setItem(this.key, JSON.stringify(recentData))
        }
      }
    } catch (error) {
      console.error('Failed to handle quota exceeded', error)
    }
  }
}
